import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import Layout from '../Components/Layout';
import 'react-toastify/dist/ReactToastify.css';

const RolesPage = () => {
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingRole, setEditingRole] = useState(null);
    const [deletingRole, setDeletingRole] = useState(null);
    const [showAddForm, setShowAddForm] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [formData, setFormData] = useState({
        name: ''
    });
    const [newRoleData, setNewRoleData] = useState({
        name: ''
    });

    useEffect(() => {
        IsAdmin();
        fetchRoles();
    }, []);
    
    const IsAdmin = async () => {
        try {
            const response = await axios.get('manage/check-role');
            if(response.data !== "Admin") {
                window.location.href = "/login";
            }
        } catch (error) {
            window.location.href = "/login";
        }
    }

    // Fetch all roles
    const fetchRoles = async () => {
        try {
            const response = await axios.get('manage/roles');
            setRoles(response.data);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching roles:', error);
            toast.error('Failed to fetch roles');
            setLoading(false);
        }
    };

    // Handle edit button click
    const handleEdit = (role) => {
        setEditingRole(role.id);
        setFormData({
            name: role.name || ''
        });
    };

    // Handle update
    const handleUpdate = async (roleId) => {
        try {
            await axios.put(`manage/roles/${roleId}`, formData);
            toast.success('Role updated successfully');
            setEditingRole(null);
            fetchRoles();
        } catch (error) {
            console.error('Error updating role:', error);
            toast.error('Failed to update role');
        }
    };

    // Handle delete
    const handleDelete = async (roleId) => {
        try {
            await axios.delete(`manage/roles/${roleId}`);
            toast.success('Role deleted successfully');
            setDeletingRole(null);
            fetchRoles();
        } catch (error) {
            console.error('Error deleting role:', error);
            toast.error('Failed to delete role');
        }
    };

    // Handle add new role
    const handleAddRole = async () => {
        try {
            if (!newRoleData.name.trim()) {
                toast.error('Role name is required');
                return;
            }
            await axios.post('manage/roles', newRoleData);
            toast.success('Role added successfully');
            setShowAddForm(false);
            setNewRoleData({ name: '' });
            fetchRoles();
        } catch (error) {
            console.error('Error adding role:', error);
            toast.error('Failed to add role');
        }
    };

    // Filter roles based on search
    const filteredRoles = roles.filter(role => 
        role.name && role.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Get user count for each role
    const getUserCount = (roleId) => {
        // This would typically come from your API
        // For now, returning placeholder
        return Math.floor(Math.random() * 10);
    };

    return (
        <Layout>
            <div style={{ height: 'calc(100vh - 70px)', overflow: 'auto', padding: '20px 0px' }}>
                <div className="container-fluid">
                    {/* Search and Add Button */}
                    <div className="row mb-4">
                        <div className="col-md-6">
                            <div className="input-group">
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Search roles..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    style={{ height: '45px' }}
                                />
                                <span className="input-group-text">
                                    <i className="ti-search"></i>
                                </span>
                            </div>
                        </div>
                        <div className="col-md-6 text-end">
                            <button 
                                className="btn btn-primary"
                                onClick={() => setShowAddForm(true)}
                                style={{ height: '45px', padding: '0 24px' }}
                            >
                                <i className="ti-plus me-2"></i>Add New Role
                            </button>
                        </div>
                    </div>

                    {/* Add Role Form */}
                    {showAddForm && (
                        <div className="card mb-4" style={{ border: '1px solid #e5e7eb' }}>
                            <div className="card-header" style={{ backgroundColor: '#f8f9fa', borderBottom: '1px solid #e5e7eb' }}>
                                <h5 className="mb-0">Add New Role</h5>
                            </div>
                            <div className="card-body">
                                <div className="row">
                                    <div className="col-md-12 mb-3">
                                        <label>Role Name</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={newRoleData.name}
                                            onChange={(e) => setNewRoleData({...newRoleData, name: e.target.value})}
                                            placeholder="Enter role name"
                                            style={{ height: '45px' }}
                                        />
                                    </div>
                                </div>
                                <div className="mt-3">
                                    <button className="btn btn-success me-2" onClick={handleAddRole}>
                                        <i className="ti-check me-1"></i> Save Role
                                    </button>
                                    <button className="btn btn-secondary" onClick={() => setShowAddForm(false)}>
                                        <i className="ti-close me-1"></i> Cancel
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Roles Grid */}
                    <div className="row">
                        {loading ? (
                            <div className="col-12 text-center p-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden"></span>
                                </div>
                            </div>
                        ) : filteredRoles.length === 0 ? (
                            <div className="col-12">
                                <div className="card" style={{ border: '1px solid #e5e7eb' }}>
                                    <div className="card-body text-center py-5">
                                        <i className="ti-id-badge" style={{ fontSize: '48px', color: '#6c757d' }}></i>
                                        <p className="mt-3 mb-0" style={{ color: '#6c757d' }}>No roles found</p>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            filteredRoles.map(role => (
                                <div key={role.id} className="col-md-6 col-lg-4 mb-4">
                                    <div className="card h-100" style={{ 
                                        border: '1px solid #e5e7eb',
                                        transition: 'all 0.3s',
                                        cursor: 'pointer'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.transform = 'translateY(-2px)';
                                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = 'translateY(0)';
                                        e.currentTarget.style.boxShadow = 'none';
                                    }}>
                                        <div className="card-body">
                                            {editingRole === role.id ? (
                                                <div>
                                                    <input
                                                        type="text"
                                                        className="form-control mb-3"
                                                        value={formData.name}
                                                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                                                        style={{ height: '45px' }}
                                                    />
                                                    <div className="d-flex gap-2">
                                                        <button 
                                                            className="btn btn-sm btn-success flex-fill"
                                                            onClick={() => handleUpdate(role.id)}
                                                        >
                                                            <i className="ti-check me-1"></i>Save
                                                        </button>
                                                        <button 
                                                            className="btn btn-sm btn-secondary flex-fill"
                                                            onClick={() => setEditingRole(null)}
                                                        >
                                                            <i className="ti-close me-1"></i>Cancel
                                                        </button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <>
                                                    <div className="d-flex justify-content-between align-items-start mb-3">
                                                        <div>
                                                            <h5 className="card-title mb-1" style={{ fontWeight: '600' }}>
                                                                {role.name}
                                                            </h5>
                                                            <p className="text-muted small mb-0">
                                                                Role ID: {role.id}
                                                            </p>
                                                        </div>
                                                        <div className="dropdown">
                                                            <button 
                                                                className="btn btn-sm btn-light"
                                                                style={{ border: 'none' }}
                                                            >
                                                                <i className="ti-more-alt"></i>
                                                            </button>
                                                        </div>
                                                    </div>
                                                    
                                                    <div className="mb-3">
                                                        <span className="badge bg-light text-dark" style={{ padding: '8px 12px' }}>
                                                            <i className="ti-user me-1"></i> {getUserCount(role.id)} Users
                                                        </span>
                                                    </div>

                                                    <div className="d-flex gap-2">
                                                        <button 
                                                            className="btn btn-sm btn-outline-primary flex-fill"
                                                            onClick={() => handleEdit(role)}
                                                        >
                                                            <i className="ti-pencil me-1"></i>Edit
                                                        </button>
                                                        <button 
                                                            className="btn btn-sm btn-outline-danger flex-fill"
                                                            onClick={() => setDeletingRole(role.id)}
                                                            disabled={role.name === 'Admin'} // Prevent deleting Admin role
                                                        >
                                                            <i className="ti-trash me-1"></i>Delete
                                                        </button>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>

                    {/* Delete Confirmation Modal */}
                    {deletingRole && (
                        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                            <div className="modal-dialog modal-dialog-centered">
                                <div className="modal-content">
                                    <div className="modal-header">
                                        <h5 className="modal-title">Confirm Delete</h5>
                                    </div>
                                    <div className="modal-body">
                                        <p>Are you sure you want to delete this role?</p>
                                        <p className="text-danger small">
                                            <i className="ti-alert me-1"></i>
                                            All users with this role will need to be reassigned.
                                        </p>
                                    </div>
                                    <div className="modal-footer">
                                        <button 
                                            className="btn btn-danger"
                                            onClick={() => handleDelete(deletingRole)}
                                        >
                                            Delete Role
                                        </button>
                                        <button 
                                            className="btn btn-secondary"
                                            onClick={() => setDeletingRole(null)}
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <ToastContainer />
        </Layout>
    );
};

export default RolesPage;